/**
 * What a ```query block says.
 *
 * Deliberately not a query language. The search line is the *same* syntax as
 * the search box — `tag:`, `path:`, `title:`, `-word`, "quoted phrases" — so
 * there is one thing to learn and one implementation to keep correct. Around it
 * sit a few `key: value` lines for how to show the result.
 *
 *     ```query
 *     tag:work -draft
 *     sort: updated desc
 *     limit: 10
 *     as: table
 *     fields: title, updated, tags
 *     ```
 *
 * With `tasks: open` the block lists the unfinished task lines of the matching
 * notes instead of the notes themselves — an inbox assembled from wherever the
 * tasks were written.
 *
 * A line that is meant as a directive but is not one is reported rather than
 * quietly searched for: `sort: sideways` is a mistake to see, not a document
 * named "sort: sideways".
 */

export type SortField = 'updated' | 'created' | 'title' | 'relevance';
export type QueryLayout = 'list' | 'table' | 'cards';
/** A built-in column, or any frontmatter key of the note. */
export type QueryField = string;
export const BUILTIN_FIELDS = ['title', 'updated', 'created', 'folder', 'tags', 'size'] as const;
export type BuiltinField = (typeof BUILTIN_FIELDS)[number];

export function isBuiltinField(field: string): field is BuiltinField {
  return (BUILTIN_FIELDS as readonly string[]).includes(field);
}

/** A frontmatter key: what a person would actually write above a note. */
const PROPERTY_RE = /^[a-z][\w-]*$/i;

export type TaskState = 'open' | 'done' | 'all';

export interface QuerySpec {
  /** The search, in the search box's own syntax. */
  search: string;
  sort: { field: SortField; descending: boolean };
  /** Whether a sort was asked for, or is the fallback. */
  sortExplicit: boolean;
  limit: number;
  layout: QueryLayout;
  fields: QueryField[];
  /** When set, the block lists task lines from the matching notes, not the notes. */
  tasks: TaskState | null;
  /** Lines that looked like directives but were not understood. */
  problems: string[];
}

export const DEFAULT_LIMIT = 25;
export const MAX_LIMIT = 200;

const SORT_FIELDS: SortField[] = ['updated', 'created', 'title', 'relevance'];
const LAYOUTS: QueryLayout[] = ['list', 'table', 'cards'];
const TASK_STATES: TaskState[] = ['open', 'done', 'all'];
const DIRECTIVES = ['sort', 'limit', 'as', 'fields', 'tasks'] as const;

const DIRECTIVE_RE = new RegExp(`^\\s*(${DIRECTIVES.join('|')})\\s*:\\s*(.*)$`, 'i');

export function parseQueryBlock(source: string): QuerySpec {
  const spec: QuerySpec = {
    search: '',
    sort: { field: 'updated', descending: true },
    sortExplicit: false,
    limit: DEFAULT_LIMIT,
    layout: 'list',
    fields: ['title', 'updated'],
    tasks: null,
    problems: [],
  };
  const searchLines: string[] = [];

  for (const line of source.split('\n')) {
    if (!line.trim()) continue;
    const directive = DIRECTIVE_RE.exec(line);
    if (!directive) {
      searchLines.push(line.trim());
      continue;
    }
    const key = (directive[1] ?? '').toLowerCase();
    const value = (directive[2] ?? '').trim();

    if (key === 'sort') {
      const [field = '', order = ''] = value.toLowerCase().split(/\s+/);
      if (!SORT_FIELDS.includes(field as SortField)) {
        spec.problems.push(`sort: ${value || '?'} — ${SORT_FIELDS.join(', ')}`);
        continue;
      }
      if (order && order !== 'asc' && order !== 'desc') {
        spec.problems.push(`sort: ${value} — asc, desc`);
        continue;
      }
      spec.sort = {
        field: field as SortField,
        // Titles read best A–Z, times newest first; an explicit order wins.
        descending: order ? order === 'desc' : field !== 'title',
      };
      spec.sortExplicit = true;
      continue;
    }

    if (key === 'limit') {
      const n = Number(value);
      if (!Number.isInteger(n) || n < 1) {
        spec.problems.push(`limit: ${value || '?'} — 1…${MAX_LIMIT}`);
        continue;
      }
      spec.limit = Math.min(n, MAX_LIMIT);
      continue;
    }

    if (key === 'tasks') {
      const state = (value || 'open').toLowerCase();
      if (!TASK_STATES.includes(state as TaskState)) {
        spec.problems.push(`tasks: ${value} — ${TASK_STATES.join(', ')}`);
        continue;
      }
      spec.tasks = state as TaskState;
      continue;
    }

    if (key === 'as') {
      const layout = value.toLowerCase();
      if (!LAYOUTS.includes(layout as QueryLayout)) {
        spec.problems.push(`as: ${value || '?'} — ${LAYOUTS.join(', ')}`);
        continue;
      }
      spec.layout = layout as QueryLayout;
      continue;
    }

    // fields: the built-in columns, or any frontmatter key of the notes —
    // which is what makes a table of your own properties possible at all.
    const wanted = value
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean);
    const unusable = wanted.filter((f) => !PROPERTY_RE.test(f));
    if (unusable.length || wanted.length === 0) {
      spec.problems.push(
        `fields: ${unusable.join(', ') || '?'} — ${BUILTIN_FIELDS.join(', ')}, or a property name`,
      );
      continue;
    }
    spec.fields = wanted.map((f) => (isBuiltinField(f.toLowerCase()) ? f.toLowerCase() : f));
  }

  spec.search = searchLines.join(' ').trim();
  return spec;
}
